import React, { Fragment, useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import { Header, Icon, LoadingIndicatorProgress } from '@ohif/ui';
import Footer from '../../components/Footer';
import { clearStudiesStorage, useAppConfig } from '@state';
import HttpClient from '@utils/HttpClient';
import { updateMenuOptions } from '@utils/navigation';
import { DeleteConfirmationModal, InviteUserModal } from './Modals';
import {
    BREAK_CARDS,
    COLORS_MAP,
    getDefaultDetailedData,
    modalityKeys,
    serviceOptions,
    statusOptions
} from './utils';

import './Workspace.css';


function filterServiceOptions(wsServices) {
    return Object.keys(serviceOptions)
        .filter(key => wsServices.includes(key))
        .reduce((obj, key) => {
            obj[key] = serviceOptions[key];
            return obj;
        }, {});
}


const FindingValueItem = ({ title, value, total, color = 'bg-blue-500' }) => {
    const { t } = useTranslation();
    const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
    const [animatedWidth, setAnimatedWidth] = useState(0);
    const isRed = color === 'bg-red-500';

    useEffect(() => {
        const timeout = setTimeout(() => setAnimatedWidth(percentage), 50);
        return () => clearTimeout(timeout);
    }, [percentage]);

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-sm capitalize font-medium workspace-text-muted">{t(`Workspaces:${title}`)}</span>
                <span className="text-lg font-bold workspace-text-title">{value}</span>
            </div>
            <div className="w-full rounded-full h-1.5 workspace-progress-track">
                <div
                    className={`h-1.5 rounded-full transition-all duration-700 ease-out ${isRed ? 'workspace-progress-red' : 'workspace-progress-blue'}`}
                    style={{ width: `${animatedWidth}%` }}
                />
            </div>
            <p className="text-xs workspace-text-subtle">{percentage}% {t('Workspaces:of total')}</p>
        </div>
    );
};


const GroupedFindingCard = ({ findingKey, findingValues, total }) => {
    const { t } = useTranslation();
    const hasOnlyTrue = Object.keys(findingValues).length === 1 && Object.keys(findingValues)[0] === 'true';

    return (
        <div className="rounded-2xl p-4 workspace-card-nested">
            {!hasOnlyTrue && (
                <h5 className="text-sm font-semibold capitalize mb-4 uppercase tracking-wider workspace-text-accent">
                    {t(`Workspaces:${findingKey}`)}
                </h5>
            )}
            <div className="space-y-4">
                {Object.entries(findingValues).map(([key, value], index) => (
                    <FindingValueItem
                        key={key + index}
                        title={hasOnlyTrue ? findingKey : key}
                        value={value}
                        total={total}
                        color={COLORS_MAP[key] || 'bg-blue-500'}
                    />
                ))}
            </div>
        </div>
    );
};


const TabButton = ({ id, title, iconPath, isActive, onClick }) => (
    <button
        onClick={() => onClick(id)}
        className={`flex flex-1 items-center justify-center sm:justify-start gap-2.5 px-4 py-2.5 rounded-xl transition-all duration-200 cursor-pointer font-semibold text-sm workspace-tab-btn hover:bg-white/[0.08] hover:border-indigo-400/40 hover:text-slate-200 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 ${isActive ? 'active' : ''}`}
    >
        <img className="min-w-7 w-10 h-10" src={iconPath} />
        <span className="hidden sm:block">{title}</span>
    </button>
);


export default function Workspace() {
    const { appConfig } = useAppConfig();
    const [isStaff] = useState(appConfig.session.isStaff);
    const cookies = new Cookies(null, { path: '/' });
    const navigate = useNavigate();
    const { workspaceId } = useParams();
    const { t } = useTranslation();

    const [examData, setExamData] = useState<{
        workspaceName: string;
        statusCounts: Record<string, number>;
        processedExams: number;
        errorExams: number;
        totalExams: number;
        detailedData: any;
    }>({
        workspaceName: '',
        statusCounts: {
            'Editado': 0,
            'Não visualizado': 0,
            'Visualizado': 0
        },
        processedExams: 0,
        errorExams: 0,
        totalExams: 0,
        detailedData: getDefaultDetailedData()
    });

    const [usersData, setUsersData] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [availableServices, setAvailableServices] = useState({});

    // Estados para o modal de confirmação
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Estados para o modal de convite
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteForm, setInviteForm] = useState<{
        emails: string[];
        services: string[];
    }>({
        emails: [],
        services: []
    });
    const [inviting, setInviting] = useState(false);

    const menuOptions = [{
        icon: 'power-off',
        title: t('Header:Logout'),
        onClick: async () => {
            const response = await HttpClient.get('/logout/');
            if (response.success) {
                navigate('/login', { replace: true })
                clearStudiesStorage()
            } else {
                console.log(response)
            }
        },
    }];
    if (isStaff) {
        updateMenuOptions(menuOptions, navigate, t)
    }

    useEffect(() => {
        async function loadData() {
            try {
                const response = await HttpClient.get(`/workspaces/${workspaceId}/workspace_data`);
                if (!response.ok) {
                    toast.error(t('Workspaces:Error loading workspace data'));
                    navigate('/not-found', { replace: true })
                    return
                }
                if (response.data.owner) {
                    const respUsers = await HttpClient.get(`/workspaces/${workspaceId}/get_users`);
                    setUsersData(respUsers.data || null);
                }
                setAvailableServices(filterServiceOptions(response.data.services));

                const data = response.data || getDefaultDetailedData();
                const { totalExams, totalErrors, totalProcessed } = modalityKeys.reduce(
                    (totals, key) => ({
                        totalExams: totals.totalExams + (data[key]?.total_objects || 0),
                        totalErrors: totals.totalErrors + (data[key]?.objects_with_errors || 0),
                        totalProcessed: totals.totalProcessed + (data[key]?.objects_without_errors || 0)
                    }),
                    { totalExams: 0, totalErrors: 0, totalProcessed: 0 }
                );

                setExamData({
                    workspaceName: response.data.name,
                    statusCounts: response.data.status_counts,
                    processedExams: totalProcessed,
                    errorExams: totalErrors,
                    totalExams: totalExams,
                    detailedData: data
                });
                setLoading(false);
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
                setUsersData(null);
                setExamData({
                    statusCounts: {
                        'Editado': 0,
                        'Não visualizado': 0,
                        'Visualizado': 0
                    },
                    workspaceName: '',
                    processedExams: 0,
                    errorExams: 0,
                    totalExams: 0,
                    detailedData: getDefaultDetailedData()
                });
                setLoading(false);
            }
        }

        document.title = 'HarpiAI APP | Workspace';
        loadData();
    }, []);

    const handleDeleteUser = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        setDeleting(true);
        try {
            await HttpClient.patch(
                `/workspaces/${workspaceId}/remove_user`,
                userToDelete,
                cookies.get('token')
            );
            setUsersData(prevUsers => prevUsers.filter(user => user.id !== userToDelete.id));
            setShowDeleteModal(false);
            setUserToDelete(null);
            toast.success(t('Workspaces:User deleted successfully'));
        } catch (error) {
            toast.error(t('Workspaces:Error deleting user'));
            console.error('Erro ao excluir usuário:', error);
        } finally {
            setDeleting(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
    };

    const handleInviteUser = () => {
        setShowInviteModal(true);
    };

    const cancelInvite = () => {
        setShowInviteModal(false);
        setInviteForm({ emails: [], services: [] });
    };

    const handleFormChange = (field, value) => {
        setInviteForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleServiceToggle = (service) => {
        setInviteForm(prev => ({
            ...prev,
            services: prev.services.includes(service)
                ? prev.services.filter(s => s !== service)
                : [...prev.services, service]
        }));
    };

    const confirmInviteUser = async (explicitEmails) => {
        const emailsToSend = Array.isArray(explicitEmails) && explicitEmails.length > 0
            ? explicitEmails
            : inviteForm.emails;

        if (!emailsToSend || emailsToSend.length === 0 || inviteForm.services.length === 0) {
            toast.error(t('Workspaces:Please enter at least one email and select a service'));
            return;
        }

        setInviting(true);
        try {
            const results = await Promise.allSettled(
                emailsToSend.map(email =>
                    HttpClient.post(
                        '/workspaces/invite_user',
                        {
                            email,
                            services: inviteForm.services,
                            workspaces: [workspaceId]
                        },
                        cookies.get('token')
                    )
                )
            );

            const succeeded = results.filter(r => r.status === 'fulfilled' && (r.value?.ok !== false && r.value?.status !== 400));
            const failedCount = emailsToSend.length - succeeded.length;

            if (succeeded.length > 0) {
                if (emailsToSend.length === 1) {
                    toast.success(t('Workspaces:Invitation sent to') + ' ' + emailsToSend[0]);
                } else {
                    toast.success(`${succeeded.length} ${t('Workspaces:invitations sent successfully')}`);
                }
                const respUsers = await HttpClient.get(`/workspaces/${workspaceId}/get_users`);
                setUsersData(respUsers.data || null);
            }

            if (failedCount > 0) {
                toast.error(`${failedCount} ${t('Workspaces:invitations failed to send')}`);
            }

            setShowInviteModal(false);
            setInviteForm({ emails: [], services: [] });
        } catch (error) {
            toast.error(t('Workspaces:Error sending invitation'));
            console.error('Erro ao enviar convite:', error);
        } finally {
            setInviting(false);
        }
    };

    const renderUsersAndServices = () => {
        const serviceGroups = usersData.reduce((acc, user) => {
            if (!availableServices[user.service]) {
                return acc;
            }
            if (!acc[user.service]) {
                acc[user.service] = [];
            }
            acc[user.service].push(user);
            return acc;
        }, {});

        return (
            <div className="rounded-2xl p-6 workspace-section-card">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-9 h-9 rounded-xl workspace-icon-badge-soft">
                            <img src="/public/assets/icons/icon-users-2.svg" width="20" height="20" />
                        </div>
                        <h4 className="text-base font-bold workspace-text-title">{t('Workspaces:Users and Services')}</h4>
                    </div>

                    <button
                        onClick={handleInviteUser}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer workspace-btn-primary hover:brightness-110 hover:shadow-[0_4px_20px_rgba(99,102,241,0.5)] hover:scale-105 active:scale-95"
                    >
                        <Icon name="icon-plus" className="w-4 h-4" />
                        <span>{t('Workspaces:Invite User')}</span>
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(serviceGroups).map(([service, users]) => (
                        <div key={service} className="rounded-xl p-4 workspace-service-group-card">
                            <div className="flex items-center gap-2.5 mb-3">
                                <img width="28" height="28" src={availableServices[service].iconPath} />
                                <div>
                                    <h5 className="text-sm font-semibold workspace-text-service-title">
                                        {t(`AdvancedFilters:${availableServices[service]?.name}`)}
                                    </h5>
                                    <p className="text-xs workspace-text-subtle">
                                        {users.length} {users.length > 1 ? t('Workspaces:users') : t('Workspaces:user')}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-1.5 max-h-80 ohif-scrollbar overflow-y-auto pr-2">
                                {users.map(user => (
                                    <div key={user.id} className="rounded-lg px-3 py-2 flex items-center justify-between workspace-user-item">
                                        <p className="text-sm font-medium workspace-text-body">{user.username}</p>
                                        <button
                                            onClick={() => handleDeleteUser(user)}
                                            className="flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-200 cursor-pointer workspace-text-danger hover:bg-red-500/20 hover:text-red-400 hover:scale-105 active:scale-95"
                                            title="Excluir usuário"
                                        >
                                            <Icon name="icon-trash" className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {usersData.length === 0 && (
                    <div className="text-center py-8">
                        <p className="workspace-text-subtle">Nenhum usuário encontrado</p>
                    </div>
                )}
            </div>
        );
    };

    const renderOverview = () => (
        <div className="space-y-6">
            <div className="rounded-2xl p-6 workspace-section-card">
                <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl workspace-icon-badge-soft">
                        <img src="/public/assets/icons/icon-overview-3.svg" width="20" height="20" />
                    </div>
                    <h3 className="text-base font-bold workspace-text-muted">
                        {t('Workspaces:WorkspaceOverview')} <span className="workspace-text-title">{examData.workspaceName}</span>
                    </h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        { value: examData.totalExams, label: t('Workspaces:Total of Studies'), statClass: 'workspace-stat-card-gray' },
                        { value: examData.processedExams, label: t('Workspaces:Success'), statClass: 'workspace-stat-card-green' },
                        { value: examData.errorExams, label: t('Workspaces:With error'), statClass: 'workspace-stat-card-red' },
                        { value: examData.totalExams > 0 ? `${Math.round((examData.processedExams / examData.totalExams) * 100)}%` : '0%', label: t('Workspaces:Success rate'), statClass: 'workspace-stat-card-indigo' },
                    ].map(({ value, label, statClass }) => (
                        <div key={label} className={`workspace-stat-card ${statClass}`}>
                            <p className="text-3xl font-bold mb-1 workspace-stat-value">{value}</p>
                            <p className="text-xs font-medium uppercase tracking-wider workspace-text-label">{label}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="rounded-2xl p-6 workspace-section-card">
                <h4 className="text-xs font-bold uppercase tracking-widest mb-4 workspace-text-accent">{t('Workspaces:Distribution by modality')}</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.values(availableServices).map(service => (
                        <div key={service.name} className="flex flex-col items-center text-center p-4 rounded-xl workspace-stat-item-card">
                            <img className="mb-2" width="32" height="32" src={service.iconPath} />
                            <p className="text-2xl font-bold workspace-text-title">{examData.detailedData[service.name.toLowerCase()]?.total_objects || 0}</p>
                            <p className="text-xs workspace-text-label">{t(`AdvancedFilters:${service.name}`)}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="rounded-2xl p-6 workspace-section-card">
                <h4 className="text-xs font-bold uppercase tracking-widest mb-4 workspace-text-accent">{t('Workspaces:Seen exams')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.values(statusOptions).map(status => (
                        <div key={status.name} className="flex flex-col items-center text-center p-4 rounded-xl workspace-stat-item-card">
                            <img src={status.iconPath} width="32" height="32" className="mb-2" />
                            <p className="text-2xl font-bold workspace-text-title">{examData.statusCounts[status.name] || 0}</p>
                            <p className="text-xs workspace-text-label">{t(`Common:Status ${status.name}`)}</p>
                        </div>
                    ))}
                </div>
            </div>

            {usersData ? renderUsersAndServices() : null}
        </div>
    );

    const renderModalityDetails = (modality, modalityData) => {
        if (!modalityData || !modalityData.findings) return null;
        const { findings, errors, total_objects: totalObjects } = modalityData;

        return (
            <div className="space-y-6">
                <div className="rounded-2xl p-6 workspace-section-card">
                    <div className="flex items-center gap-3 mb-6">
                        <img className="rounded-lg" width="32" height="32" src={modality.iconPath} />
                        <h3 className="text-lg font-bold workspace-text-title">{t(`AdvancedFilters:${modality.name}`)}</h3>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { value: totalObjects, label: t('Workspaces:Total of Studies'), statClass: 'workspace-stat-card-gray' },
                            { value: modalityData.objects_without_errors, label: t('Workspaces:Success'), statClass: 'workspace-stat-card-green' },
                            { value: modalityData.objects_with_errors, label: t('Workspaces:With error'), statClass: 'workspace-stat-card-red' },
                            { value: totalObjects > 0 ? `${Math.round((modalityData.objects_without_errors / totalObjects) * 100)}%` : '0%', label: t('Workspaces:Success rate'), statClass: 'workspace-stat-card-indigo' },
                        ].map(({ value, label, statClass }) => (
                            <div key={label} className={`workspace-stat-card ${statClass}`}>
                                <p className="text-3xl font-bold mb-1 workspace-stat-value">{value}</p>
                                <p className="text-xs font-medium uppercase tracking-wider workspace-text-label">{label}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-2xl p-6 workspace-section-card">
                    <h4 className="text-xs font-bold uppercase tracking-widest mb-4 workspace-text-accent">{t('Workspaces:Clinical Findings')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(findings).map(([findingKey, findingValues], index) => (
                            <Fragment key={findingKey + index}>
                                <GroupedFindingCard findingKey={findingKey} findingValues={findingValues} total={totalObjects} />
                                {(BREAK_CARDS[findingKey] ? [...Array(BREAK_CARDS[findingKey])] : []).map((x, i) => <div key={i} />)}
                            </Fragment>
                        ))}
                    </div>
                </div>

                <div className="rounded-2xl p-6 workspace-section-card-danger">
                    <h4 className="text-xs font-bold uppercase tracking-widest mb-4 workspace-text-danger">{t('Workspaces:Errors')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(errors).map(([findingKey, findingValues], index) => (
                            <div key={index} className="rounded-xl p-4 workspace-error-item-card">
                                <FindingValueItem title={t(`Workspaces:${findingKey}`)} value={findingValues} total={totalObjects} color="bg-red-500" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'cct':
                return renderModalityDetails(serviceOptions.cct, examData.detailedData?.cct);
            case 'cxr':
                return renderModalityDetails(serviceOptions.cxr, examData.detailedData?.cxr);
            case 'do':
                return renderModalityDetails(serviceOptions.do, examData.detailedData?.dxa);
            // case 'hct':
            //     return renderModalityDetails(serviceOptions.hct, examData.detailedData?.hct);
            case 'mammo':
                return renderModalityDetails(serviceOptions.mammo, examData.detailedData?.mammo);
            case 'mskxr':
                return renderModalityDetails(serviceOptions.mskxr, examData.detailedData?.mskxr);
            default:
                return renderOverview();
        }
    };


    if (loading) {
        return <LoadingIndicatorProgress className="h-full w-full bg-black" />
    }

    return (
        <div className="flex h-screen flex-col bg-[#070811]">
            <Header
                isSticky
                menuOptions={menuOptions}
                isReturnEnabled={true}
                WhiteLabeling={appConfig.whiteLabeling}
                onClickReturnButton={() => navigate(-1)}
            />

            <div className="p-4 ohif-scrollbar flex grow flex-col overflow-y-auto">
                <div className="container max-w-[1420px] relative m-auto mt-0">
                    <div className="mb-6">
                        <div className="flex flex-wrap gap-4">
                            <TabButton
                                id="overview"
                                title={t('Workspaces:Overview')}
                                iconPath="/public/assets/icons/icon-overview-3.svg"
                                isActive={activeTab === 'overview'}
                                onClick={setActiveTab}
                            />

                            <>
                                {Object.entries(availableServices).map(([key, service]) => (
                                    <TabButton
                                        key={key}
                                        id={key}
                                        title={t(`AdvancedFilters:${service.name}`)}
                                        iconPath={service.iconPath}
                                        isActive={activeTab === key}
                                        onClick={setActiveTab}
                                    />
                                ))}
                            </>
                        </div>
                    </div>

                    <div className="mb-8">
                        {renderContent()}
                    </div>
                </div>
            </div>

            <Footer />

            <DeleteConfirmationModal
                cancelDelete={cancelDelete}
                confirmDeleteUser={confirmDeleteUser}
                deleting={deleting}
                showDeleteModal={showDeleteModal}
                userToDelete={userToDelete}
            />

            <InviteUserModal
                availableServices={availableServices}
                cancelInvite={cancelInvite}
                confirmInviteUser={confirmInviteUser}
                handleFormChange={handleFormChange}
                handleServiceToggle={handleServiceToggle}
                inviting={inviting}
                inviteForm={inviteForm}
                showInviteModal={showInviteModal}
            />
        </div>
    );
};
